const API_BASE = import.meta.env.VITE_API_URL || '';

interface ReqParams<TBody = undefined> {
  method: 'POST' | 'GET' | 'PATCH' | 'PUT' | 'DELETE';
  route: string;
  body?: TBody;
  headers?: HeadersInit;
  uri?: string;
  credentials?: RequestCredentials;
  signal?: AbortSignal;
}

/**
 * Generic Fetch Wrapper
 * TResponse: The expected shape of the returned JSON
 * TBody: The shape of the request payload
 */
export async function Req<TResponse, TBody = undefined>({
  method,
  route,
  body,
  headers,
  uri = API_BASE,
  credentials = 'omit',
  signal,
}: ReqParams<TBody>): Promise<TResponse> {
  const url = `${uri}${route}`;
  const response = await fetch(url, {
    method: method,
    credentials, // <-- This is the magic word
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const r = response;

  // Explicitly catch HTTP errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(
      errorData?.message ||
        `HTTP Request failed with status ${response.status}`,
    );
  }

  // Parse and return the JSON directly
  return response.json();
}

/**
 * Abstract Base Class
 * Forces extending classes to implement the static route property at compile time.
 */
export abstract class ImplAPI {
  // TypeScript currently doesn't support abstract static properties perfectly,
  // but we can define the signature.
  static get route(): string {
    throw new Error("Property 'route' must be overridden in the subclass.");
  }
}
